import { findMe, formatTime, formatLongDay } from '../functions/index.js'

const _firstName = (name) => (name || '').split(' ')[0] || 'there'

// Rolling 7-day window from `now`, filtered to where the signed-in user
// is an attendee.
const _weekEvents = (s) => {
  const me = s.root.currentUserEmail
  const now = Date.now()
  const endMs = now + 7 * 24 * 60 * 60 * 1000
  return (s.root.intranetEvents || [])
    .filter(e => {
      const t = e.startAt ? new Date(e.startAt).getTime() : 0
      if (t < now || t >= endMs) return false
      return Array.isArray(e.attendees) ? e.attendees.includes(me) : true
    })
    .sort((x, y) => new Date(x.startAt).getTime() - new Date(y.startAt).getTime())
}

let _clusterCache = { peopleRef: null, me: null, value: [] }
const _teamClusters = (s) => {
  const people = s.root.people
  const me = s.root.currentUserEmail
  if (_clusterCache.peopleRef === people && _clusterCache.me === me) return _clusterCache.value
  const buckets = new Map()
  for (const p of (people || [])) {
    if (p.email === me) continue
    const key = p.location || 'Remote'
    if (!buckets.has(key)) buckets.set(key, { city: key, timezone: p.timezone, members: [] })
    buckets.get(key).members.push(p)
  }
  const value = [...buckets.values()].slice(0, 12)
  _clusterCache = { peopleRef: people, me, value }
  return value
}

const _section = (icon, intro, body) => ({
  flow: 'x',
  align: 'flex-start',
  gap: 'B',

  IconCol: {
    width: 'C',
    minWidth: 'C',
    align: 'flex-start center',
    color: 'caption',
    SectionIcon: { extends: 'Icon', icon, boxSize: 'B' }
  },

  Right: {
    flow: 'y',
    gap: 'A',
    flex: '1',

    Intro: {
      flow: 'x',
      gap: 'Z',
      align: 'baseline',
      flexWrap: 'wrap',
      childExtends: 'IntroRun',
      childrenAs: 'state',
      children: (el, s) => (typeof intro === 'function' ? intro(s) : intro)
    },

    Body: body
  }
})

export const main = {
  extends: 'Page',
  metadata: { title: 'Home / Symbols Workspace' },
  flow: 'y',
  padding: 'D C',
  gap: 'E',
  maxWidth: '1480px',
  margin: '0 auto',
  width: '100%',

  Greeting: {
    tag: 'h1',
    fontSize: 'F',
    fontWeight: '300',
    color: 'title',
    letterSpacing: '-0.03em',
    lineHeight: '1.1',
    text: (el, s) => `Good morning ${_firstName(findMe(s).name)}!`
  },

  TodaySection: _section(
    'clock',
    // Both as a function so the date string isn't frozen at module load.
    () => [
      { text: 'Today is ', color: 'caption' },
      { text: formatLongDay(Date.now()), color: 'title', weight: '700' }
    ],
    {
      flow: 'x',
      gap: 'Z',
      align: 'baseline',
      Now: { tag: 'span', color: 'title', fontWeight: '700', fontSize: 'A', text: (el, s) => formatTime(Date.now(), findMe(s).timezone) },
      In: { tag: 'span', color: 'caption', fontSize: 'A', text: (el, s) => `in ${findMe(s).location || 'your city'}` }
    }
  ),

  EventsSection: _section(
    'date',
    (s) => {
      const n = _weekEvents(s).length
      return [
        { text: 'You have ', color: 'caption' },
        { text: `${n} event${n === 1 ? '' : 's'}`, color: 'green', weight: '700' },
        { text: ' coming up ', color: 'caption' },
        { text: 'in the next 7 days', color: 'title', weight: '700' }
      ]
    },
    {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: 'B',
      childExtends: 'UpcomingEventCard',
      childrenAs: 'state',
      children: (el, s) => _weekEvents(s)
    }
  ),

  StoriesSection: _section(
    'star',
    (s) => {
      const stories = s.root.stories || []
      const tasks = stories.reduce((a, x) => a + (x.tasks || 0), 0)
      return [
        { text: 'You have ', color: 'caption' },
        { text: `${stories.length} stor${stories.length === 1 ? 'y' : 'ies'}`, color: 'blue', weight: '700' },
        { text: ' with ', color: 'caption' },
        { text: `${tasks} task${tasks === 1 ? '' : 's'}`, color: 'green', weight: '700' }
      ]
    },
    {
      flow: 'y',
      gap: 'B',
      childExtends: 'StoryCard',
      childrenAs: 'state',
      children: (el, s) => s.root.stories || []
    }
  ),

  BirthdaysSection: _section(
    'magicstar',
    [
      { text: 'Whom to wish ', color: 'caption' },
      { text: 'happy birthday', color: 'title', weight: '700' }
    ],
    {
      display: 'grid',
      // auto-fit instead of fixed 5 columns — child `minWidth: 'G'` on
      // CityCluster / BirthdayItem prevented `minmax(0, 1fr)` from
      // compressing, causing horizontal overflow at <1500px viewports.
      // `min(100%, 180px)` lets columns shrink to viewport on phones.
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
      gap: 'D',
      childExtends: 'BirthdayItem',
      childrenAs: 'state',
      children: (el, s) => s.root.birthdays || []
    }
  ),

  TeamTimeSection: _section(
    'globe',
    [
      { text: 'Time for your ', color: 'caption' },
      { text: 'team members', color: 'title', weight: '700' }
    ],
    {
      display: 'grid',
      // Same auto-fit fix as BirthdaysSection — child clusters have
      // their own `minWidth` which fought a fixed 5-column grid.
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
      gap: 'D',
      childExtends: 'CityCluster',
      childrenAs: 'state',
      children: (el, s) => _teamClusters(s)
    }
  )
}
